import { DRIVER_BATA, MIN_ONEWAY_KM } from './bookingConfig';
import { SERVICE_CITY_NAMES } from './coimbatoreRouteMaster';

/**
 * Promotional starting fare shown on homepage featured cards only.
 * Not used by booking, Distance Matrix, or getVehiclePrice.
 */
export const FEATURED_STARTING_FARE_DISPLAY = 'From ₹2,599';

const VEHICLES_LABEL = 'Sedan • SUV • Crysta • Crysta';

const buildFaqs = (origin, destination, direction) => {
  const routeLabel = `${origin} to ${destination}`;
  const hillNote = 'This corridor is a Nilgiris hill run, so allow for slower traffic and check weather before you travel.';

  return [
    {
      question: `How do I book a ${routeLabel} taxi on this page?`,
      answer: `Use the booking card on this ${routeLabel} page. Pickup defaults to ${origin} and drop to ${destination}. Add your 10-digit mobile number, tap Get Fare Estimate, then choose Sedan, SUV or Crysta with your date and time. Confirmation uses the same Easy Drop Taxi process as the homepage.`,
    },
    {
      question: direction === 'forward'
        ? 'Is Coimbatore to Ooty one way by default?'
        : 'Is this page for the Ooty to Coimbatore return?',
      answer: direction === 'forward'
        ? `Yes. One Way is the default so you can stay in Ooty without holding a return cab. ${hillNote} Switch to Round Trip if you need both legs, or open Ooty to Coimbatore Taxi for the downhill return later.`
        : `Yes. This page is for leaving Ooty toward Coimbatore. ${hillNote} Keep One Way selected unless the same cab must return, or use Coimbatore to Ooty Taxi for the outbound climb.`,
    },
    {
      question: `Can I change the ${origin} pickup or ${destination} drop?`,
      answer: `Yes. Both fields stay editable. Keep the city defaults or type a more specific lodge, hotel, airport or mapped point before you estimate.`,
    },
    {
      question: `How is the ${routeLabel} fare calculated?`,
      answer: `The form uses billed distance × published vehicle rate + ₹${DRIVER_BATA} driver bata, with a ${MIN_ONEWAY_KM} km one-way minimum. Distance comes from the live mapped drive for your pickup and drop — not a separate Ooty package price on this page. Toll, parking, permit, hill and waiting charges are extra when they apply.`,
    },
    {
      question: `Which vehicles can I choose for ${routeLabel}?`,
      answer: `Sedan, SUV and Crysta appear in the booking form. After Get Fare Estimate, each card shows the live-distance total plus driver bata. Larger groups can ask about a Crysta when the team confirms the booking.`,
    },
    {
      question: `When should I book ${routeLabel}?`,
      answer: `Weekend and holiday hill traffic is often busier. After the estimate, enter a future pickup date and time for leaving ${origin}, then confirm the review so the team can arrange the vehicle.`,
    },
  ];
};

const buildLandingPage = ({ origin, destination, slug, reverseSlug, direction }) => ({
  slug,
  reverseSlug,
  origin,
  destination,
  displayOrigin: origin,
  displayDestination: destination,
  distanceKm: null,
  direction,
  pairIndex: 0,
  hub: 'Coimbatore',
  title: `${origin} to ${destination} Taxi`,
  h1: `${origin} to ${destination} Taxi`,
  seoTitle: `${origin} to ${destination} Taxi | One Way Cab | Easy Drop Taxi`,
  seoDescription: direction === 'forward'
    ? 'Book a one-way Coimbatore to Ooty taxi with Easy Drop Taxi. Pickup and drop stay editable, and the fare uses the live mapped distance in the existing booking form.'
    : 'Book a one-way Ooty to Coimbatore taxi with Easy Drop Taxi. Estimate the fare online from the live route distance — not a separate package price.',
  heroLead: direction === 'forward'
    ? 'Leave Coimbatore for Ooty in a one-way cab, with pickup and drop you can edit before you estimate the fare.'
    : 'Start in Ooty and ride back toward Coimbatore without booking an unused return from the hills.',
  intro: direction === 'forward'
    ? [
      'A Coimbatore to Ooty taxi is often booked for a hill-station trip when you want a door-to-door cab instead of changing vehicles on the way up. Easy Drop Taxi treats this as a standard one-way outstation booking from the same form used on the homepage.',
      'Set pickup where you actually start in Coimbatore — home, hotel or the airport — and an Ooty drop that matches your stay. The fields stay editable if you need a more specific address.',
      'After the fare estimate, add the date and time you want to leave. Confirmation uses the existing Easy Drop Taxi flow rather than a separate Ooty package fare.',
    ]
    : [
      'Heading back from Ooty to Coimbatore is the downhill one-way leg. One-way booking means you do not have to keep a cab in Ooty overnight unless you choose Round Trip.',
      'Pickup starts as Ooty and drop as Coimbatore. Change either field if you are leaving from a lodge, a viewpoint, or finishing at a specific Coimbatore address or the airport.',
      'Choose Sedan, SUV or Crysta, then enter when you want to start. The request is sent through the same booking system as every other Easy Drop Taxi trip.',
    ],
  travel: direction === 'forward'
    ? [
      'This is a hill-station outstation ride from Coimbatore toward Ooty. You stay in one vehicle until the drop you entered.',
      'It works for a solo traveller, a couple or a family, depending on the vehicle. One-way booking means you do not have to arrange a second cab in Ooty unless you later want a round trip.',
      'Distance and travel time on this page come from the same live mapping used to estimate the fare. They are not taken from a catalogue kilometre. If mapping is unavailable, use Get Fare Estimate in the booking card.',
    ]
    : [
      'The return is booked as Ooty pickup and Coimbatore drop, with no mid-journey change unless you ask for a different address.',
      'It works for a solo traveller, a couple or a family, depending on the vehicle. Because this page is the reverse journey, you book only the Ooty to Coimbatore leg unless you switch the form to Round Trip.',
      'Distance and travel time on this page come from the same live mapping used to estimate the fare for Ooty to Coimbatore. The reverse direction is mapped on its own page.',
    ],
  faqs: buildFaqs(origin, destination, direction),
  originInCityList: SERVICE_CITY_NAMES.has(origin),
  destinationInCityList: SERVICE_CITY_NAMES.has(destination),
  ctaTitle: `Ready to Travel from ${origin} to ${destination}?`,
  ctaBody: `Book your ${origin} to ${destination} taxi with EasyDropTaxi.`,
});

export const FEATURED_ROUTE_LANDING_PAGES = [
  buildLandingPage({
    origin: 'Coimbatore',
    destination: 'Ooty',
    slug: '/coimbatore-to-ooty',
    reverseSlug: '/ooty-to-coimbatore',
    direction: 'forward',
  }),
  buildLandingPage({
    origin: 'Ooty',
    destination: 'Coimbatore',
    slug: '/ooty-to-coimbatore',
    reverseSlug: '/coimbatore-to-ooty',
    direction: 'reverse',
  }),
];

export const FEATURED_ROUTE_PROMOTIONS = [
  {
    id: 'coimbatore-ooty',
    badge: '🔥 POPULAR ROUTE',
    heading: 'Coimbatore ↔ Ooty Taxi',
    supportingText: 'One Way Taxi • Round Trip • Airport Transfer',
    legs: [
      {
        id: 'coimbatore-to-ooty',
        title: 'Coimbatore → Ooty',
        tripTypeLabel: 'One Way Taxi',
        origin: 'Coimbatore',
        destination: 'Ooty',
        detailsPath: '/coimbatore-to-ooty',
        detailsLabel: 'View Coimbatore to Ooty taxi route details',
        startingFareDisplay: FEATURED_STARTING_FARE_DISPLAY,
        vehiclesLabel: VEHICLES_LABEL,
      },
      {
        id: 'ooty-to-coimbatore',
        title: 'Ooty → Coimbatore',
        tripTypeLabel: 'One Way Taxi',
        origin: 'Ooty',
        destination: 'Coimbatore',
        detailsPath: '/ooty-to-coimbatore',
        detailsLabel: 'View Ooty to Coimbatore taxi route details',
        startingFareDisplay: FEATURED_STARTING_FARE_DISPLAY,
        vehiclesLabel: VEHICLES_LABEL,
      },
    ],
  },
];
